import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FilterList } from '@mui/icons-material';
import PropTypes from 'prop-types';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/**
 * Column header for a "Status" column that renders the heading text plus a
 * filter icon. Clicking the icon opens a simple Active / Inactive / All
 * dropdown that drives filtering directly (no operator/value panel).
 *
 * Meant to be used from a DataGrid column's `renderHeader`, with the current
 * value/onChange lifted into the page so the grid rows can be filtered.
 */
const StatusFilterHeader = ({ label = 'Status', value = 'all', onChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box
        component="span"
        sx={{
          fontWeight: 'bold',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Box>
      <IconButton
        size="small"
        aria-label="Filter by status"
        onClick={handleOpen}
        sx={{ color: value !== 'all' ? theme.palette.primary.main : 'inherit' }}
      >
        <FilterList fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {STATUS_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={value === option.value}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

StatusFilterHeader.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOf(['all', 'active', 'inactive']),
  onChange: PropTypes.func.isRequired,
};

export default StatusFilterHeader;

/**
 * Helper to apply a status filter value to a set of rows.
 * Filters on the row's `isActive` boolean.
 */
export const applyStatusFilter = (rows, statusFilter) => {
  if (statusFilter === 'active') return rows.filter((r) => r?.isActive === true);
  if (statusFilter === 'inactive') return rows.filter((r) => r?.isActive !== true);
  return rows;
};
